import {
  Feature,
  isDevelopmentEnvironment,
  isStagingEnvironment,
} from 'shared/services/logger/logger.helper'
import {
  isSystemError,
  isUserError,
  SystemError,
  UserError,
  isK2Error,
  type ErrorMetadata,
} from 'shared/utils/errors'

/**
 * Imported statically because the server adapter pulls in `@opentelemetry/api`
 * only, which is safe in either runtime. The *client* registry is reached
 * through a dynamic `import()` further down instead — this service is imported
 * by hundreds of files across both runtimes, and a static import there would
 * drag the RUM SDK into every server bundle.
 */
import { getServerReporter } from './reporter/server-instance'

/**
 * These would be additional bits of information that we could add to logged error that would give us
 *   an easier time debugging or reproducing the error
 */
export type ErrorOptions = {
  // a standardized feature tag to make grouping errors or finding like-errors easier
  feature?: Feature

  /**
   * a quick way to tag the specific operation that failed, so like-errors group together
   * - This could be a specific feature, function, or unique id to help us find the source
   * @example - `load-project-details`
   */
  key?: string

  // Searchable attributes on the reported error.
  // @warning - keep keys short and values low-cardinality so grouping stays useful
  tags?: Record<string, string>

  // Any extra context to attach to the reported error.
  extra?: Record<string, string | number | boolean | undefined>
}

function sanitizeError(
  error: unknown,
  options?: ErrorOptions,
): SystemError | UserError {
  const md: ErrorMetadata = {
    ...(options?.tags ?? {}),
    ...(options?.extra ?? {}),
    operationName: options?.key,
    feature: options?.feature,
  }

  if (isUserError(error) || isSystemError(error)) {
    error.metadata = {
      ...error.metadata,
      ...md,
    }

    return error
  }

  if (isK2Error(error)) {
    const e = error.toApplicationError()
    e.metadata = {
      ...e.metadata,
      ...md,
    }

    return e
  }

  if (error instanceof Error)
    return new SystemError(error.message, {
      ...md,
      originalError: error,
    })

  if (typeof error === 'string') return new SystemError(error, { ...md })

  return new SystemError('unknown error type', { ...md })
}

/**
 * Log generic errors.
 * - actual `Error` types are preferred, so we get a full stack trace
 * - As errors from try/catches may not always be an error, we'll handle that ambiguity for you
 * - When NEXT_PUBLIC_APP_ENV is production, we report through the registered ErrorReporter
 *     Otherwise, we just print the error to the console
 * - The error's class name becomes the reported error type, so `new SystemError("msg")`
 *     surfaces as "SystemError: msg". Grouping uses `ApplicationError.fingerprint`.
 * - Errors can be enhanced with additional context by passing in an `ErrorOptions` object as a loggerOptions property
 * - Errors can also be ignored by setting the `handled` property to true on the error
 */
export function error(inputError: unknown, options?: ErrorOptions): void {
  const error = sanitizeError(inputError, options)

  if (isDevelopmentEnvironment() || isStagingEnvironment()) {
    console.error(error.message, {
      ...error.toLogContext(),
      stack: error.stack,
    })

    return
  }

  if (error.shouldReport()) {
    reportError(error)
  }
}

/**
 * Hands the error to the reporter registered for this runtime.
 *
 * The client registry is reached through a dynamic `import()` rather than a
 * static one: `shared/services/logger` is imported by ~470 files across both
 * runtimes, and a static import would drag the browser RUM SDK into every
 * server bundle. Fire-and-forget, so `logger.error` stays synchronous.
 */
function reportError(error: SystemError | UserError): void {
  if (typeof window === 'undefined') {
    getServerReporter().report(error)
    return
  }

  void import('./reporter/client-instance')
    .then(({ getClientReporter }) => getClientReporter().report(error))
    .catch((importError: unknown) => {
      console.error('Client error reporting failed', {
        originalMessage: error.message,
        stack: importError instanceof Error ? importError.stack : undefined,
      })
    })
}

/**
 * a wrapper for console.debug
 * - This is never reported to an error reporter
 */
export function debug(message: string): void {
  if (isDevelopmentEnvironment()) {
    console.debug(message)
  }
}

/**
 * a wrapper for console.info
 * - This is never reported to an error reporter
 */
export function info(message: string): void {
  if (isDevelopmentEnvironment() || isStagingEnvironment()) {
    console.info(message)
  }
}

/**
 * Logging errors in Next.js can be a bit of a pain since sometimes it will be
 * a client-error, and other times it will be a server error. Add that we have
 * different tools to handling client and server errors, and this becomes more
 * of a pain.
 *
 * This logging service is intended to abstract all of that detail from you so
 * you can safely use the same API whether you're on the client or server and
 * this will take care of reporting the errors the correct service.
 */
const logger = {
  error,
  debug,
  info,
  Feature,
}

export default logger
